import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  removeConnections,
  useAllIndicatorsMin,
  useClient,
} from '../../apollo';
import {
  Indicator,
  INDICATOR_STAGES_KEY,
  IndicatorByService,
  IndicatorGroup,
  IndicatorSectionKey,
  indicatorSectionsInOrder,
  IndicatorWithUpdate,
} from 'npo/pages/ReportBuilder/Components/ImpactIndicator/utils';
import {
  IndicatorDetails,
  SectionIndicators,
} from 'npo/pages/ReportBuilder/Components/IndicatorDetails/utils';
import {
  indicatorFileDescriptionInitialState,
} from 'npo/pages/ReportBuilder/Components/IndicatorDetails/IndicatorDetailQuestions/IndicatorFileUploadRender';
import { updateSuccessCriteria } from 'npo/pages/ReportBuilder/Components/ImpactIndicator/api';
import { useGraphQLRequest } from '../../auth/hooks/useApiRequest';
import { numberWithCommas } from '../../utils/numberUtils';
import {
  deleteReportIndicator,
  getReportIndicatorDetails,
  getReportIndicators,
} from './api';
import AppErrorMessage from '../../components/AppErrorMessage';
import {
  getAllUnselectedIndicators,
  IndicatorContext,
  isModelEmpty,
  parseAnswer,
  ResultsByReport,
  ResultsIndicator,
  serviceQuestionId,
  subServiceQuestionId,
  capacityDevelopmentQuestionId,
  beneficiaryTermQuestionId,
  intermediariesQuestionId,
  socialImpactQuestionId,
  reachQuestionId,
  learnQuestionId,
  actQuestionId,
  DeletedIndicator,
  getModal,
  parseReportAnswers,
  SimpleAnswersById,
  deduplicateNpoIndicators,
  IndicatorsByStage,
  uniqueIndicators,
  processReportResults,
} from './utils';
import { getAnswersBySectionId } from 'donor/portal/pages/FeedbackDashboard/Feedback/api';
import {
  answersBySection,
  ReportAnswers,
} from 'donor/portal/pages/FeedbackDashboard/Feedback/FeedbackReport';
import { updateBulkAnswer } from 'npo/pages/NPOFeedback/MakeEdits/api';
import { getBeneficiaryQuestionText } from 'npo/pages/ReportBuilder/Components/SectionMultiRadio';
import { requestInit } from '../../auth/hooks/useRequestState';
import {
  getReportAnswers,
  ReportAnswersResponse,
} from 'npo/pages/BetaFeedback/api';
import {
  deleteImpactSummaryIndicators,
  getDeletedIndicatorsFromLocalStorage,
  LocalDeletedIndicators,
} from 'npo/pages/NPOFeedback/MakeEdits/components/ImpactTable/utils';
import { getReachIndicator } from 'npo/pages/ReportBuilder/Components/IndicatorDetails/IndicatorDetailQuestions/utils';
import { DATES_QUESTION_ID_1 } from 'npo/pages/ReportBuilder/Components/ReportFinalize/answersUtil';
import {
  parseReportPeriod,
  ReportPeriod,
} from 'npo/pages/ReportBuilder/Components/SectionPeriod/utils';
import {
  getPublicAnswersAndResults,
  PublicAnswersAndResultsResponse,
} from '../../auth/ui/PublicReportApi';

const replaceIndicatorFile = (
  indicator: IndicatorDetails,
  answers: SimpleAnswersById,
): IndicatorDetails => {
  if (indicator.file) return indicator;
  const result = { ...indicator };
  const answer = answers[50001];
  if (answer && typeof answer === 'object') {
    const i = answer[INDICATOR_STAGES_KEY[result.stageId]].find(
      x => x.indicatorId === result.indicatorId,
    );
    if (i) {
      result.file = i.file;
    }
  }
  return result;
};

const combineIndicators = (
  indicatorsByStage: IndicatorsByStage,
  indicators: Indicator[],
) => {
  return uniqueIndicators([
    ...indicators,
    ...indicatorsByStage.reach,
    ...indicatorsByStage.succeed,
    ...indicatorsByStage.capacityDevelopment,
  ]);
};

export const useIndicator = () => useContext(IndicatorContext);

export const IndicatorProvider: React.FC<{
  children: React.ReactNode;
  publicToken?: string;
}> = ({ children, publicToken }) => {
  const client = useClient();
  const { data } = useAllIndicatorsMin();
  const [savingState, makeSavingRequest, setSavingState] = useGraphQLRequest();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [answersBySections, setAnswersBySections] = useState<ReportAnswers>({});
  const [beneficiaryName, setBeneficiaryName] = useState<string | undefined>();
  const [intermediaries, setIntermediaries] = useState<string | undefined>();
  const [allServices, setAllServices] = useState<string[]>([]);
  const [indicatorsByService, setIndicatorsByService] = useState<{
    [service: string]: Indicator[] | undefined;
  }>();
  const [reportNpoIndicators, setReportNpoIndicators] = useState<
    IndicatorDetails[]
  >([]);
  const [reportResults, setReportResults] = useState<ResultsIndicator[]>([]);
  const [capacityDevelopment, setCapacityDevelopment] = useState<
    string[] | undefined
  >();
  const [reach, setReach] = useState<string[] | undefined>();
  const [act, setAct] = useState<'Yes' | 'No' | undefined>();
  const [learn, setLearn] = useState<'Yes' | 'No' | undefined>();
  const [succeed, setSucceed] = useState<string[] | undefined>();
  const [deletedIndicators, setDeletedIndicators] = useState<DeletedIndicator>(
    {},
  );
  const [reportId, setReportId] = useState<string>();
  const [service, setService] = useState<string | undefined>();
  const [answers, setAnswers] = useState<SimpleAnswersById>({});
  const [allAnswers, setAllAnswers] = useState<ReportAnswersResponse>({
    stage: 'INITIAL',
    status: 'Draft',
    answers: [],
  });
  const [subService, setSubService] = useState<string | undefined>();
  const [reportPeriod, setReportPeriod] = useState<ReportPeriod>({
    card: undefined,
    startDate: undefined,
    endDate: undefined,
  });
  const [saveRequestState, makeSaveRequest] = useGraphQLRequest();
  const [impactIndicators, setImpactIndicators] = useState<ResultsByReport>({});
  const [detailIndicators, setDetailIndicators] = useState<{
    [id: number]: IndicatorDetails | undefined;
  }>({});
  const [localDeletedIndicators, setLocalDeletedIndicators] = useState<
    LocalDeletedIndicators
  >(getDeletedIndicatorsFromLocalStorage(reportId));
  const [indicatorsByStage, setIndicatorsByStage] = useState<IndicatorsByStage>(
    {
      capacityDevelopment: [],
      reach: [],
      learn: [],
      act: [],
      succeed: [],
      catalyzed: [],
    },
  );
  const [initialIndicators, setInitialIndicators] = useState<
    IndicatorByService
  >({
    capacityDevelopment: [],
    reach: [],
    learn: [],
    act: [],
    succeed: [],
    catalyzed: [],
  });
  const [allIndicators, setAllIndicators] = useState<IndicatorByService>({
    capacityDevelopment: [],
    reach: [],
    learn: [],
    act: [],
    succeed: [],
    catalyzed: [],
  });
  const [impactSummary, setImpactSummary] = useState<IndicatorByService>({
    capacityDevelopment: [],
    reach: [],
    learn: [],
    act: [],
    succeed: [],
    catalyzed: [],
  });
  const [impactDetails, setImpactDetails] = useState<SectionIndicators>({
    capacityDevelopment: [],
    reach: [],
    learn: [],
    act: [],
    succeed: [],
    catalyzed: [],
  });
  const [loadingDetailsIndicators, setLoadingDetailsIndicators] = useState<
    boolean
  >(false);
  const [loadingImpactIndicators, setLoadingImpactIndicators] = useState<
    boolean
  >(false);

  // Refs mirror the latest values so loadReportIndicators can stay stable
  // (avoids infinite-loop when callers include it in useEffect deps).
  const reportIdRef = useRef(reportId);
  const loadingImpactIndicatorsRef = useRef(loadingImpactIndicators);
  const loadingDetailsIndicatorsRef = useRef(loadingDetailsIndicators);
  useEffect(() => {
    reportIdRef.current = reportId;
  }, [reportId]);
  useEffect(() => {
    loadingImpactIndicatorsRef.current = loadingImpactIndicators;
  }, [loadingImpactIndicators]);
  useEffect(() => {
    loadingDetailsIndicatorsRef.current = loadingDetailsIndicators;
  }, [loadingDetailsIndicators]);

  useEffect(() => {
    if (savingState.type === 'REQUEST_SUCCESS') {
      setSavingState(requestInit());
    }
  }, [savingState.type]);

  useEffect(() => {
    const indicatorsData = data as IndicatorGroup[] | undefined;
    if (!indicatorsData || indicatorsData.length === 0 || indicatorsByService) {
      return;
    }
    const services: string[] = [];
    const indicatorsByStage: IndicatorsByStage = {
      capacityDevelopment: [],
      reach: [],
      learn: [],
      act: [],
      succeed: [],
      catalyzed: [],
    };
    const result: { [service: string]: Indicator[] } = {};
    indicatorsData.forEach(indicator => {
      services.push(indicator.name);
      result[indicator.name] = indicator.indicators;
      indicator.indicators.forEach(indicator => {
        indicatorsByStage[INDICATOR_STAGES_KEY[indicator.stageId]].push(
          indicator,
        );
      });
    });
    for (const stage of indicatorSectionsInOrder) {
      indicatorsByStage[stage] = uniqueIndicators(indicatorsByStage[stage]);
    }
    setAllServices(services);
    setIndicatorsByService(result);
    setIndicatorsByStage(indicatorsByStage);
  }, [data]);

  // const reloadAnswers = useCallback(() => {
  //   if (!reportId || !client) return;
  //   makeRequest(getReportAnswers(client, reportId));
  // }, [reportId, client]);

  // useEffect(() => {
  //   if (requestState.type === 'REQUEST_INIT') {
  //     reloadAnswers();
  //   } else if (requestState.type === 'REQUEST_SUCCESS') {
  //     setAnswers(parseReportAnswers(requestState.data?.allReports[0]));
  //     setLoadingReportAnswers(false);
  //   }
  // }, [requestState, reportId]);

  useEffect(() => {
    if (
      !isModelEmpty(allIndicators) ||
      !subService ||
      !indicatorsByService ||
      !capacityDevelopment ||
      !learn ||
      !act
    ) {
      return;
    }
    const indicators = indicatorsByService[subService];
    if (!indicators) {
      return;
    }
    const indicatorsWithSucceed = combineIndicators(
      indicatorsByStage,
      indicators,
    );
    const i = getAllUnselectedIndicators(
      indicatorsWithSucceed,
      impactSummary,
      capacityDevelopment.length > 0,
      learn === 'Yes',
      act === 'Yes',
    );
    setInitialIndicators(i.all);
    setAllIndicators(i.unselected);
  }, [subService, capacityDevelopment, learn, act]);

  const handleUpdateBeneficiaryTerms = useCallback(
    (beneficiaryTerm: string, intermediaries: string | undefined) => {
      setBeneficiaryName(beneficiaryTerm);
      setIntermediaries(intermediaries);
      if (!client || !reportId) return;
      const result = [
        {
          answerId: undefined,
          text: beneficiaryTerm,
          questionId: beneficiaryTermQuestionId,
        },
        {
          answerId: undefined,
          text: intermediaries
            ? getBeneficiaryQuestionText(beneficiaryTerm, 1)
            : getBeneficiaryQuestionText(beneficiaryTerm, 0),
          questionId: 40006,
        },
      ];
      intermediaries &&
        result.push({
          answerId: undefined,
          text: intermediaries,
          questionId: intermediariesQuestionId,
        });
      makeSavingRequest(updateBulkAnswer(client, reportId, result));
    },
    [reportId, setBeneficiaryName, setIntermediaries],
  );

  const generateModel = useCallback(() => {
    if (
      !indicatorsByService ||
      !subService ||
      !capacityDevelopment ||
      !reach ||
      !learn ||
      !act ||
      !succeed
    ) {
      return;
    }
    const indicators = indicatorsByService[subService];

    if (!indicators) {
      return;
    }
    const indicatorsWithSucceed = combineIndicators(
      indicatorsByStage,
      indicators,
    );
    const result = getModal(
      indicatorsWithSucceed,
      deletedIndicators,
      capacityDevelopment,
      reach,
      learn,
      act,
      succeed,
      impactIndicators,
      detailIndicators,
    );
    setImpactSummary(result.summary);
    setImpactDetails(result.details);
    setAllIndicators(result.unselectedIndicators);
    return result;
  }, [
    indicatorsByService,
    subService,
    capacityDevelopment,
    reach,
    learn,
    act,
    succeed,
    deletedIndicators,
    impactIndicators,
    detailIndicators,
    setImpactSummary,
    setImpactDetails,
  ]);

  useEffect(() => {
    if (
      !reportId ||
      !client ||
      !indicatorsByService ||
      loadingDetailsIndicators ||
      loadingImpactIndicators
    ) {
      return;
    }
    getAnswersBySectionId(client, reportId).then(r => {
      const result = removeConnections(r.data.allNpoReportAnswers) || [];
      const answers = answersBySection(result);
      setAnswersBySections(answers);
      const service = answers.impact_summary?.find(
        i => i.question.dbId === serviceQuestionId,
      )?.text;
      const subService = answers.impact_summary?.find(
        i => i.question.dbId === subServiceQuestionId,
      )?.text;
      const capacityDevelopment = parseAnswer(
        answers.impact_summary?.find(
          i => i.question.dbId === capacityDevelopmentQuestionId,
        )?.text,
      );
      const reach = parseAnswer(
        answers.impact_summary?.find(i => i.question.dbId === reachQuestionId)
          ?.text,
      );
      const learn = answers.impact_summary?.find(
        i => i.question.dbId === learnQuestionId,
      )?.text;
      const act = answers.impact_summary?.find(
        i => i.question.dbId === actQuestionId,
      )?.text;
      const succeed = answers.impact_summary?.find(
        i => i.question.dbId === socialImpactQuestionId,
      )?.text;
      const beneficiaryName = answers.impact_summary?.find(
        i => i.question.dbId === beneficiaryTermQuestionId,
      )?.text;
      const intermediaries = answers.impact_summary?.find(
        i => i.question.dbId === intermediariesQuestionId,
      )?.text;
      const reportPeriod = answers.dates?.find(
        x => x.question.dbId === DATES_QUESTION_ID_1,
      )?.text;
      setService(service);
      setSubService(subService);
      setReach(reach);
      setCapacityDevelopment(capacityDevelopment);
      setSucceed(parseAnswer(succeed));
      setLearn(learn ? (learn === 'Yes' ? 'Yes' : 'No') : undefined);
      setAct(act ? (act === 'Yes' ? 'Yes' : 'No') : undefined);
      setBeneficiaryName(beneficiaryName);
      setIntermediaries(intermediaries);
      setReportPeriod(parseReportPeriod(reportPeriod));
      const indicators =
        subService && indicatorsByService[subService]
          ? indicatorsByService[subService]
          : [];
      if (subService && indicators.length > 0) {
        const indicatorsWithSucceed = combineIndicators(
          indicatorsByStage,
          indicators,
        );
        const i = getAllUnselectedIndicators(
          indicatorsWithSucceed,
          impactSummary,
          capacityDevelopment.length > 0,
          learn === 'Yes',
          act === 'Yes',
        );
        setInitialIndicators(i.all);
        setAllIndicators(i.unselected);
      }
    });
  }, [
    reportId,
    client,
    indicatorsByService,
    loadingDetailsIndicators,
    loadingImpactIndicators,
  ]);

  const handleUpdateDescription = useCallback(
    async (
      indicatorId: number,
      sectionKey: IndicatorSectionKey,
      value: string,
    ): Promise<boolean> => {
      if (!reportId) {
        return false;
      }
      const request = updateSuccessCriteria(
        client!,
        reportId,
        indicatorId,
        true,
        value,
      );
      makeSaveRequest(request);
      const r = await request;
      if (!r.data?.addSuccessCriteria?.success) {
        return false;
      }
      setImpactSummary(s => {
        const newState = { ...s };
        const index = newState[sectionKey].findIndex(i => i.id === indicatorId);
        if (index < 0) return s;
        newState[sectionKey][index] = {
          ...newState[sectionKey][index],
          updatedText: value,
        };
        return newState;
      });
      return true;
    },
    [reportId, setImpactSummary, makeSaveRequest],
  );

  const isModalEmpty = useCallback((): boolean => {
    return (
      impactSummary.succeed.length === 0 &&
      impactSummary.reach.length === 0 &&
      impactSummary.learn.length === 0 &&
      impactSummary.act.length === 0 &&
      impactSummary.capacityDevelopment.length === 0
    );
  }, [impactSummary]);

  const loadReportIndicators = useCallback(
    async (r: number | string, forceLoad = false) => {
      const id = r.toString();
      const shouldLoad = forceLoad ? true : !reportIdRef.current;

      if (
        !client ||
        !shouldLoad ||
        loadingImpactIndicatorsRef.current ||
        loadingDetailsIndicatorsRef.current
      ) {
        return;
      }
      reportIdRef.current = id;
      loadingImpactIndicatorsRef.current = true;
      loadingDetailsIndicatorsRef.current = true;
      setReportId(id);
      setLoadingImpactIndicators(true);
      setLoadingDetailsIndicators(true);
      setLocalDeletedIndicators(getDeletedIndicatorsFromLocalStorage(id));
      // When publicToken is set, both answers and results come from a single
      // public API call. Otherwise, fall back to the authenticated endpoint.
      const publicData: PublicAnswersAndResultsResponse | undefined =
        publicToken !== undefined
          ? (await getPublicAnswersAndResults(client, id, publicToken)).data
          : undefined;
      const answersArray: ReportAnswersResponse | undefined = publicData
        ? (({
            answers: removeConnections(publicData.allNpoReportAnswers),
          } as unknown) as ReportAnswersResponse)
        : removeConnections(
            (await getReportAnswers(client, id)).data.allReports,
          )[0];
      answersArray && setAllAnswers(answersArray);
      const answers = parseReportAnswers(answersArray);
      setAnswers(answers);
      setAnswersBySections(answersBySection(answersArray?.answers || []));
      getReportIndicatorDetails(client, id)
        .then(r => {
          const response: IndicatorDetails[] = removeConnections(
            r.data.allNpoReportIndicators,
          );
          const allNpoReportIndicators = deduplicateNpoIndicators(response);
          setReportNpoIndicators(allNpoReportIndicators);
          const details: SectionIndicators = {
            capacityDevelopment: [],
            reach: [],
            learn: [],
            act: [],
            succeed: [],
            catalyzed: [],
          };
          allNpoReportIndicators.forEach(indicator => {
            details[INDICATOR_STAGES_KEY[indicator.stageId]].push(
              replaceIndicatorFile(indicator, answers),
            );
          });
          setDetailIndicators(
            allNpoReportIndicators.reduce((acc, indicator) => {
              acc[indicator.indicatorId] = indicator;
              return acc;
            }, {} as { [id: number]: IndicatorDetails }),
          );
          setLoadingDetailsIndicators(false);
        })
        .catch(() => {
          setLoadingDetailsIndicators(false);
        });
      const resultsPromise = publicData
        ? Promise.resolve(removeConnections(publicData.publicReportResults))
        : getReportIndicators(client, id).then(r =>
            removeConnections(r.data.allResults),
          );

      resultsPromise
        .then(flatResults => {
          const {
            reportResults,
            indicatorsByReport,
            summary,
            details,
            deleteIndicators,
          } = processReportResults(flatResults, answers);
          setReportResults(reportResults);
          setImpactDetails(details);
          setImpactSummary(summary);
          setDeletedIndicators(deleteIndicators);
          setImpactIndicators(indicatorsByReport);
          setLoadingImpactIndicators(false);
          publicToken && setLoadingDetailsIndicators(false);
        })
        .catch(() => {
          setLoadingImpactIndicators(false);
        });
    },
    [client, publicToken],
  );

  const getMissingIndicatorDetails = useCallback(
    (
      indicatorId: number,
      sectionKey: IndicatorSectionKey,
      indicator: IndicatorWithUpdate,
      impactSummaryIndex: number,
    ) => {
      const detail: IndicatorDetails = {
        indicatorId,
        indicatorDefinition: indicator.definition,
        indicatorName: indicator.name,
        stageId: indicator.stageId,
        isHuman: indicator.isHuman,
        file: undefined,
        forecast: undefined,
        timePeriod: undefined,
        measurement: undefined,
        finalForecast: undefined,
        relevant: true,
        level1Forecast: undefined,
        level1Guess: undefined,
        level1IsHuman: undefined,
        level1ForecastResultOne: undefined,
      };
      setImpactDetails(s => {
        const newState = { ...s };
        newState[sectionKey].splice(impactSummaryIndex, 0, detail);
        return newState;
      });
    },
    [setImpactDetails],
  );

  const getSourcesAndAssumptions = useCallback(
    (indicator: IndicatorDetails): string => {
      const description = indicatorFileDescriptionInitialState(indicator);
      if (description) {
        return description;
      }
      const i = impactIndicators[indicator.indicatorId];
      if (i?.description) {
        return i.description;
      }
      const detail = impactDetails[
        INDICATOR_STAGES_KEY[indicator.stageId]
      ].find(x => x.indicatorId === indicator.indicatorId);
      if (!detail) return '';
      const d = indicatorFileDescriptionInitialState(detail);
      return d || '';
    },
    [impactIndicators, impactDetails],
  );

  const getRenderForecast = useCallback(
    (indicator: IndicatorDetails): string => {
      const forecast = indicator.finalForecast || indicator.forecast;
      return forecast ? numberWithCommas(forecast) : '';
    },
    [],
  );

  const handleAddIndicator = useCallback(
    (sectionKey: IndicatorSectionKey, indicator: IndicatorWithUpdate) => {
      const indicatorId = indicator.id;
      const oldData = deletedIndicators[indicatorId];
      setAllIndicators(s => {
        const newState = { ...s };
        const index = newState[sectionKey].findIndex(i => i.id === indicatorId);
        if (index < 0) return s;
        newState[sectionKey].splice(index, 1);
        return newState;
      });
      if (oldData) {
        setDeletedIndicators(s => {
          const newState = { ...s };
          delete newState[indicatorId];
          return newState;
        });
        setImpactSummary(s => {
          const newState = { ...s };
          newState[sectionKey].push(oldData.indicatorSummary);
          return newState;
        });
        setImpactDetails(s => {
          const newState = { ...s };
          newState[sectionKey].push(oldData.indicatorDetails);
          return newState;
        });
        return;
      }
      setImpactSummary(s => {
        const newState = { ...s };
        newState[sectionKey].push({ ...indicator, seen: true });
        return newState;
      });
      setImpactDetails(s => {
        const newState = { ...s };
        newState[sectionKey].push({
          indicatorId,
          indicatorDefinition: indicator.definition,
          indicatorName: indicator.name,
          stageId: indicator.stageId,
          isHuman: indicator.isHuman,
          file: undefined,
          forecast: undefined,
          measurement: undefined,
          finalForecast: undefined,
          relevant: true,
          level1Forecast: undefined,
          level1Guess: undefined,
          level1IsHuman: undefined,
          level1ForecastResultOne: undefined,
        });
        return newState;
      });
    },
    [
      deletedIndicators,
      setDeletedIndicators,
      setImpactSummary,
      setImpactDetails,
      setAllIndicators,
    ],
  );

  const handleRestoreIndicator = useCallback(
    (
      sectionKey: IndicatorSectionKey,
      indicatorId: number,
      summary?: IndicatorWithUpdate,
      detail?: IndicatorDetails,
    ) => {
      const stored = deletedIndicators[indicatorId];
      const summaryToRestore = stored?.indicatorSummary ?? summary;
      const detailToRestore = stored?.indicatorDetails ?? detail;
      if (!summaryToRestore || !detailToRestore) return;
      setAllIndicators(s => {
        const newState = { ...s };
        const index = newState[sectionKey].findIndex(i => i.id === indicatorId);
        if (index < 0) return s;
        newState[sectionKey].splice(index, 1);
        return newState;
      });
      setImpactSummary(s => {
        const newState = { ...s };
        if (newState[sectionKey].some(i => i.id === indicatorId)) return s;
        newState[sectionKey] = [...newState[sectionKey], summaryToRestore];
        return newState;
      });
      setImpactDetails(s => {
        const newState = { ...s };
        const existing = newState[sectionKey].find(
          i => i.indicatorId === indicatorId,
        );
        if (existing) {
          existing.file = detailToRestore.file;
          existing.forecast = detailToRestore.forecast;
          existing.timePeriod = detailToRestore.timePeriod;
          existing.measurement = detailToRestore.measurement;
          existing.finalForecast = detailToRestore.finalForecast;
          return newState;
        }
        newState[sectionKey] = [...newState[sectionKey], detailToRestore];
        return newState;
      });
      setDeletedIndicators(s => {
        const newState = { ...s };
        delete newState[indicatorId];
        return newState;
      });
    },
    [
      deletedIndicators,
      setAllIndicators,
      setImpactSummary,
      setImpactDetails,
      setDeletedIndicators,
    ],
  );

  const handleDeleteIndicator = useCallback(
    async (sectionKey: IndicatorSectionKey, indicatorId: number) => {
      const summary = impactSummary[sectionKey].find(i => i.id === indicatorId);
      const detail = impactDetails[sectionKey].find(
        i => i.indicatorId === indicatorId,
      );
      if (!client || !reportId || !summary || !detail) return;

      // Optimistic UI update
      setAllIndicators(s => {
        if (!summary) return s;
        const newState = { ...s };
        newState[sectionKey].push(summary);
        return newState;
      });
      if (summary && detail) {
        setDeletedIndicators(s => {
          const newState = { ...s };
          newState[indicatorId] = {
            indicatorSummary: summary,
            indicatorDetails: detail,
          };
          return newState;
        });
      }
      setImpactSummary(s => {
        const newState = { ...s };
        const index = newState[sectionKey].findIndex(i => i.id === indicatorId);
        if (index < 0) return s;
        newState[sectionKey].splice(index, 1);
        return newState;
      });
      setImpactDetails(s => {
        const newState = { ...s };
        const index = newState[sectionKey].findIndex(
          i => i.indicatorId === indicatorId,
        );
        if (index < 0) return s;
        newState[sectionKey].splice(index, 1);
        return newState;
      });
      try {
        const response = await deleteReportIndicator(
          client,
          reportId,
          indicatorId,
        );
        if (!response.data?.deleteReportIndicatorByIds?.success) {
          throw new Error('Server did not confirm deletion');
        }
      } catch (e) {
        // Rollback the optimistic delete.
        handleRestoreIndicator(sectionKey, indicatorId, summary, detail);
        const message =
          (e as Error)?.message ||
          'Failed to delete indicator. Please try again.';
        setErrorMessage(
          `Failed to delete indicator: ${message}. Please try again.`,
        );
      }
    },
    [
      client,
      reportId,
      impactDetails,
      impactSummary,
      handleRestoreIndicator,
      setDeletedIndicators,
      setImpactSummary,
      setImpactDetails,
      setAllIndicators,
    ],
  );

  const handleDeleteSection = useCallback(
    (sectionKey: IndicatorSectionKey) => {
      deleteImpactSummaryIndicators(
        sectionKey,
        impactSummary[sectionKey],
        impactDetails[sectionKey],
        setLocalDeletedIndicators,
      );
      setAllIndicators(s => {
        const newState = { ...s };
        newState[sectionKey] = initialIndicators[sectionKey];
        return newState;
      });
      setImpactDetails(s => {
        const newState = { ...s };
        newState[sectionKey] = [];
        return newState;
      });
      setImpactSummary(s => {
        const newState = { ...s };
        newState[sectionKey] = [];
        return newState;
      });
    },
    [
      initialIndicators,
      impactSummary,
      impactDetails,
      setImpactSummary,
      setImpactDetails,
    ],
  );

  const setIndicatorState = useCallback(
    (questionId: number, value: string[]) => {
      switch (questionId) {
        case capacityDevelopmentQuestionId:
          return setCapacityDevelopment(value);
        case reachQuestionId:
          return setReach(value);
        case socialImpactQuestionId:
          return setSucceed(value);
        default:
          return;
      }
    },
    [setCapacityDevelopment, setReach, setSucceed],
  );

  const setLearnAndAct = useCallback(
    (questionId: number, value: 'Yes' | 'No' | undefined) => {
      switch (questionId) {
        case learnQuestionId:
          return setLearn(value);
        case actQuestionId:
          return setAct(value);
        default:
          return;
      }
    },
    [setLearn, setAct],
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).debug = () => ({
    service,
    subService,
    reportId,
    impactIndicators,
    allIndicators,
    impactSummary,
    impactDetails,
    deletedIndicators,
    indicatorsByService,
    capacityDevelopment,
    reach,
    learn,
    act,
    succeed,
    beneficiaryName,
    intermediaries,
    generateModel,
  });

  return (
    <IndicatorContext.Provider
      value={{
        isLoading: loadingImpactIndicators || loadingDetailsIndicators,
        savingState,
        answers,
        allAnswers,
        answersBySections,
        impactSummary,
        impactDetails,
        allIndicators,
        service,
        subService,
        reportId,
        localDeletedIndicators,
        deletedIndicators,
        saveRequestState,
        indicators: impactIndicators,
        reportNpoIndicators,
        reportResults,
        serviceQuestionId,
        subServiceQuestionId,
        capacityDevelopment,
        reach,
        learn,
        act,
        succeed,
        beneficiaryName,
        intermediaries,
        capacityDevelopmentQuestionId,
        beneficiaryTermQuestionId,
        intermediariesQuestionId,
        socialImpactQuestionId,
        reachQuestionId,
        learnQuestionId,
        actQuestionId,
        reportPeriod,
        reachHumanIndicator: impactDetails
          ? getReachIndicator(impactDetails.reach)
          : undefined,
        isModalEmpty,
        getMissingIndicatorDetails,
        handleDeleteSection,
        setAllIndicators,
        setImpactSummary,
        setImpactDetails,
        generateModel,
        setIndicatorState,
        setLearnAndAct,
        setBeneficiaryName,
        setIntermediaries,
        setCapacityDevelopment,
        setReach,
        setLearn,
        setAct,
        setSucceed,
        setService,
        setSubService,
        loadReportIndicators,
        getSourcesAndAssumptions,
        handleUpdateDescription,
        getRenderForecast,
        handleAddIndicator,
        handleDeleteIndicator,
        handleRestoreIndicator,
        handleUpdateBeneficiaryTerms,
      }}
    >
      {children}
      <AppErrorMessage
        open={Boolean(errorMessage)}
        message={errorMessage}
        onClose={() => setErrorMessage(null)}
      />
    </IndicatorContext.Provider>
  );
};
